import { IStorageAdapter, LocalStorageAdapter, SessionStorageAdapter, THEME_MODE_TYPE, THEME_MODES } from "@web3auth/auth";
import { computed, ref } from "vue";

import config from "../config";
import { analytics } from "../utils/analytics";
import { LOGIN_MODE, LOGIN_MODE_TYPE, StorageUtils } from "../utils/interfaces";
import { getIFrameOriginObject, getUserLanguage, isMain } from "../utils/utils";

const loginMode = ref<LOGIN_MODE_TYPE>(LOGIN_MODE.main);

const namespace = ref<string>("");

const dashboardApplicationMap = ref<Record<string, string>>({});

const AUTH_SESSION_ID_STORAGE_SUFFIX = ":session_id";

const USER_PREFERENCE_KEYS = {
  theme: ":ws-theme",
  locale: ":ws-locale",
  currency: ":ws-currency",
} as const;

const browserStorageMap = ref<Map<string, IStorageAdapter>>(
  new Map([
    ["local", new LocalStorageAdapter()],
    ["session", new SessionStorageAdapter()],
  ])
);

export default () => {
  const sessionNamespace = computed(() => {
    if (config.sessionNamespace) return config.sessionNamespace;
    if (namespace.value) return namespace.value;
    if (loginMode.value === LOGIN_MODE.embed) return getIFrameOriginObject().hostname;
    return "";
  });

  const storageUtils = computed<StorageUtils>(() => {
    let storage: Storage | undefined;
    if (loginMode.value !== LOGIN_MODE.plugin) {
      storage = config.storageAvailability.local ? window.localStorage : undefined;
    } else {
      storage = config.storageAvailability.session ? window.sessionStorage : undefined;
    }
    return {
      storage,
      storageType: loginMode.value !== LOGIN_MODE.plugin ? "local" : "session",
      storageKey: sessionNamespace.value ? `ws-${sessionNamespace.value}` : "ws-wallet",
      authStoreKey: sessionNamespace.value ? `auth_store_${sessionNamespace.value}` : "auth_store",
    };
  });

  const getAuthSessionIdStorageKey = (): string => {
    return `${storageUtils.value.authStoreKey}${AUTH_SESSION_ID_STORAGE_SUFFIX}`;
  };

  const userPreferenceStorageKey = (kind: keyof typeof USER_PREFERENCE_KEYS): string =>
    `${storageUtils.value.authStoreKey}${USER_PREFERENCE_KEYS[kind]}`;

  async function readUserPreference(kind: keyof typeof USER_PREFERENCE_KEYS): Promise<string | undefined | null> {
    if (!isMain) return null;
    if (!config.storageAvailability[storageUtils.value.storageType]) return null;
    const storage = browserStorageMap.value.get(storageUtils.value.storageType);
    if (!storage) return null;
    const raw = await storage.get(userPreferenceStorageKey(kind));
    return raw as string | undefined;
  }

  async function writeUserPreference(kind: keyof typeof USER_PREFERENCE_KEYS, value: string): Promise<void> {
    if (!isMain) return;
    if (!config.storageAvailability[storageUtils.value.storageType]) return;
    const storage = browserStorageMap.value.get(storageUtils.value.storageType);
    if (!storage) return;
    await storage.set(userPreferenceStorageKey(kind), value);
  }

  async function getSessionIdFromStorage(): Promise<string> {
    if (!config.storageAvailability[storageUtils.value.storageType]) return "";
    const storage = browserStorageMap.value.get(storageUtils.value.storageType);
    if (!storage) return "";

    const authSessionIdKey = getAuthSessionIdStorageKey();
    const storedSessionId = await storage.get(authSessionIdKey);
    if (storedSessionId) return storedSessionId;

    return "";
  }

  async function setSessionIdToStorage(sessionId: string): Promise<void> {
    if (!config.storageAvailability[storageUtils.value.storageType]) return;
    const storage = browserStorageMap.value.get(storageUtils.value.storageType);
    if (!storage) return;

    await storage.set(getAuthSessionIdStorageKey(), sessionId);
  }

  async function getUserThemeFromStorage(): Promise<THEME_MODE_TYPE | null> {
    const raw = await readUserPreference("theme");
    if (raw === null) return null;
    return (raw as THEME_MODE_TYPE) || THEME_MODES.light;
  }

  async function setUserThemeToStorage(theme: THEME_MODE_TYPE): Promise<void> {
    await writeUserPreference("theme", theme);
  }

  async function getUserLocaleFromStorage(): Promise<string | null> {
    const raw = await readUserPreference("locale");
    if (raw === null) return null;
    return raw || getUserLanguage();
  }

  async function setUserLocaleToStorage(locale: string): Promise<void> {
    await writeUserPreference("locale", locale);
  }

  async function getUserCurrencyFromStorage(): Promise<string | null> {
    const raw = await readUserPreference("currency");
    if (raw === null) return null;
    return raw?.toLowerCase() || "usd";
  }

  async function setUserCurrencyToStorage(currency: string): Promise<void> {
    await writeUserPreference("currency", currency.toLowerCase());
  }

  async function setTheme(theme: THEME_MODE_TYPE) {
    if (theme === THEME_MODES.light) {
      document.documentElement.classList.remove("dark");
    } else if (theme === THEME_MODES.dark) {
      document.documentElement.classList.add("dark");
    } else if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }

    document.querySelector("html").style.colorScheme = document.documentElement.classList.contains("dark") ? "dark" : "light";

    await setUserThemeToStorage(theme);
  }

  function setLoginMode(mode: LOGIN_MODE_TYPE) {
    loginMode.value = mode;
    analytics.setGlobalProperties({ login_mode: mode });
  }

  function setNamespace(value: string) {
    namespace.value = value;
  }

  function addDashboardApplication(dappData: { dappId: number; address: string }) {
    dashboardApplicationMap.value[dappData.dappId] = dappData.address;
  }

  return {
    storageUtils,
    loginMode,
    sessionNamespace,
    dashboardApplicationMap,
    getSessionIdFromStorage,
    getUserThemeFromStorage,
    getUserLocaleFromStorage,
    setUserLocaleToStorage,
    getUserCurrencyFromStorage,
    setUserCurrencyToStorage,
    setTheme,
    setLoginMode,
    setNamespace,
    addDashboardApplication,
    setSessionIdToStorage,
  };
};
